import React, { useState, useEffect } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Divider,
  ListItemIcon
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Person as PersonIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  AdminPanelSettings as AdminIcon,
  Logout as LogoutIcon
  ,Feedback as FeedbackIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '@/store';
import { logout } from '@/api/auth';
import { useCurrentUserSettingsQuery } from '@/features/userSettings/hooks/useUserSettingsQuery';
import PendingRequestsBadge from './PendingRequestsBadge';

interface UserSettingsMenuProps {
  anchorOrigin?: {
    vertical: 'top' | 'bottom';
    horizontal: 'left' | 'right';
  };
  transformOrigin?: {
    vertical: 'top' | 'bottom';
    horizontal: 'left' | 'right';
  };
  hasModelAccess?: boolean;
  trigger?: React.ReactNode; // Added trigger prop
  // Control which menu items are shown
  showThemeToggle?: boolean;
  showUserSettings?: boolean;
  showFeedback?: boolean;
  showAdminPanel?: boolean;
  showLogout?: boolean;
  // Control whether divider before logout is shown
  showDivider?: boolean;
}

const UserSettingsMenu: React.FC<UserSettingsMenuProps> = ({
  anchorOrigin = { vertical: 'bottom', horizontal: 'right' },
  transformOrigin = { vertical: 'top', horizontal: 'right' },
  hasModelAccess = true,
  trigger, // Added trigger prop
  showThemeToggle = true,
  showUserSettings = true,
  showFeedback = true,
  showAdminPanel = true,
  showLogout = true,
  showDivider = true,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const {
    data: currentUserSettings,
    isError: isUserSettingsError,
    error: userSettingsError,
  } = useCurrentUserSettingsQuery();

  const { themeMode, toggleTheme, pendingPermissionRequestsCount } = useAppStore();

  const hasAdminAccess =
    currentUserSettings?.role === 'admin' || currentUserSettings?.role === 'manager' || currentUserSettings?.is_admin === true;

  useEffect(() => {
    if (isUserSettingsError) {
      console.error('Error checking admin access:', userSettingsError);
    }
  }, [isUserSettingsError, userSettingsError]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOpenAdminPanel = () => {
    setAnchorEl(null);
    navigate('/admin');
  };

  const handleOpenUserSettings = () => {
    setAnchorEl(null);
    navigate('/user-settings');
  };

  const handleSendFeedback = () => {
    setAnchorEl(null);
    // Open SharePoint feedback form in a new tab
    const url = 'https://bosnet.sharepoint.com/:l:/s/Innovation_Technology/JAC3MN96GytCQbFCBec0hbOaAfYBYVnHJMpga6rYDU5lfnA?nav=MmI0ODM0MGQtOTU3Zi00OTU5LWE1MDQtZGYyM2FiNWE4Zjky';
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleToggleTheme = () => {
    toggleTheme();
    handleClose();
  };

  // const handleRefreshProfilePicture = async () => {
  //   try {
  //     const updatedSettings = await refreshProfilePicture();
  //     // Update the user's pictureVersion in the store
  //     if (user) {
  //       useAppStore.setState({
  //         user: {
  //           ...user,
  //           pictureVersion: updatedSettings.picture_version || Date.now()
  //         }
  //       });
  //     }
  //     handleClose();
  //   } catch (error) {
  //     console.error('Error refreshing profile picture:', error);
  //   }
  // };

  const handleLogout = async () => {
    try {
      await logout();
      // Reload the page to clear state and redirect to login
      window.location.reload();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Determine whether to show the divider before logout.
  // Show divider only if there is at least one visible menu item above logout
  // other than Admin Panel (or if Admin Panel isn't the only one).
  const hasVisibleItemsBeforeLogout = () => {
    const items: boolean[] = [];
    if (showThemeToggle) items.push(true);
    if (showUserSettings) items.push(true);
    if (showFeedback) items.push(true);
    // Admin panel is considered last-group item; include it as well
    if (showAdminPanel && hasAdminAccess) items.push(true);
    // We want to show divider if there is more than zero items and
    // not only the admin panel (i.e., length > 1 or (length===1 and that one is not admin)).
    // Simpler: count the non-admin visible items
    let nonAdminCount = 0;
    if (showThemeToggle) nonAdminCount++;
    if (showUserSettings) nonAdminCount++;
    if (showFeedback) nonAdminCount++;
    return nonAdminCount > 0;
  };

  return (
    <>
      {trigger ? (
        <div onClick={handleClick}>{trigger}</div> // Use trigger if provided
      ) : (
        <IconButton
          onClick={handleClick}
          size="small"
          disabled={!hasModelAccess}
          sx={{
            ml: 2,
            color: 'primary.main',
            opacity: hasModelAccess ? 1 : 0.5,
            '&:hover': {
              backgroundColor: hasModelAccess ? 'rgba(255, 255, 255, 0.08)' : 'transparent'
            }
          }}
          aria-controls={Boolean(anchorEl) ? 'user-settings-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={Boolean(anchorEl) ? 'true' : undefined}
        >
          <SettingsIcon />
        </IconButton>
      )}

      <Menu
        anchorEl={anchorEl}
        id="user-settings-menu"
        open={Boolean(anchorEl)}
        onClose={handleClose}
        disableAutoFocusItem
        PaperProps={{
          elevation: 0,
          sx: {
            // color: 'primary.main',
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 0.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              // bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        MenuListProps={{
          'aria-labelledby': 'user-settings-button',
          disablePadding: false,
        }}
        transformOrigin={transformOrigin}
        anchorOrigin={anchorOrigin}
      >
        {showThemeToggle && (
          <MenuItem onClick={handleToggleTheme}>
            <ListItemIcon>
              {themeMode === 'light' ? (
                <DarkModeIcon fontSize="small" />
              ) : (
                <LightModeIcon fontSize="small" />
              )}
            </ListItemIcon>
            <Typography variant="inherit">
              {themeMode === 'light' ? 'Dark Mode' : 'Light Mode'}
            </Typography>
          </MenuItem>
        )}

        {showUserSettings && (
          <MenuItem onClick={handleOpenUserSettings}>
            <ListItemIcon>
              <PersonIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">User Settings</Typography>
          </MenuItem>
        )}

        {showFeedback && (
          <MenuItem onClick={handleSendFeedback}>
            <ListItemIcon>
              <FeedbackIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Send Feedback</Typography>
          </MenuItem>
        )}

        {/* Refresh Profile Picture - Kept for future use if needed
        <MenuItem onClick={handleRefreshProfilePicture}>
          <ListItemIcon>
            <RefreshIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="inherit">Refresh Profile Picture</Typography>
        </MenuItem>
        */}

        {showAdminPanel && hasAdminAccess && (
          <MenuItem onClick={handleOpenAdminPanel}>
            <ListItemIcon>
              <AdminIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit" sx={{ display: 'inline-flex', alignItems: 'center' }}>
              Admin Panel
              {pendingPermissionRequestsCount > 0 && (
                <PendingRequestsBadge count={pendingPermissionRequestsCount} />
              )}
            </Typography>
          </MenuItem>
        )}

        {showDivider && hasVisibleItemsBeforeLogout() && <Divider />}
        
        {showLogout && (
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Logout</Typography>
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default UserSettingsMenu;
